import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import socket from "./../ScoreboardAdmin/socketClient";
import DetermineWinnerModalJJCombat from "./DetermineWinnerModalJJCombat";

import {
  createEmptyJJScore,
  addTechnique,
  removeTechnique,
  addPenalty,
  removePenalty,
  setForfait,
  getJJPoints,
  calculateJJCombatResult,
} from "./../../utils/jjCombatScoring";

/* ------------------------- Helpers génériques ------------------------- */

const PART_CONFIG = {
  1: {
    label: "Partie 1",
    description: "Atémis pieds-poings",
  },
  2: {
    label: "Partie 2",
    description: "Projections / Amenées au sol, Clés & étranglements debout",
  },
  3: {
    label: "Partie 3",
    description: "Immobilisations, Clés & étranglements au sol",
  },
};

const getCompetiteurDisplayName = (
  competiteur,
  fallbackLabel = "Combattant",
) => {
  if (!competiteur) return fallbackLabel;
  const nom = competiteur.nom_competiteur || competiteur.nom || "";
  const prenom = competiteur.prenom_competiteur || competiteur.prenom || "";
  const full = `${nom} ${prenom}`.trim();
  return full || fallbackLabel;
};

const formatSeconds = (sec) => {
  const total = Math.max(0, Number(sec) || 0);
  const m = String(Math.floor(total / 60)).padStart(2, "0");
  const s = String(total % 60).padStart(2, "0");
  return `${m}:${s}`;
};

const sanitizeCombatLabel = (value = "") => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^\d+_\d+$/.test(raw)) return "";
  return raw;
};

function buildDetailsCombatJJ({
  redScore,
  blueScore,
  result,
  competiteur1,
  competiteur2,
  chrono,
  chronoMax,
  isExtraPeriod = false,
}) {
  const redName = getCompetiteurDisplayName(competiteur1, "Rouge");
  const blueName = getCompetiteurDisplayName(competiteur2, "Bleu");

  const redPts = getJJPoints(redScore);
  const bluePts = getJJPoints(blueScore);

  const totalRed = redPts.total;
  const totalBlue = bluePts.total;

  const elapsed = chronoMax != null ? chronoMax - chrono : null;
  const chronoStr =
    elapsed != null
      ? `${formatSeconds(elapsed)} / ${formatSeconds(chronoMax)}`
      : formatSeconds(chrono);

  let gagnant = "À déterminer";
  let raison = "";

  if (redScore.forfait && !blueScore.forfait) {
    gagnant = `${blueName} (Bleu)`;
    raison = "Victoire par forfait de Rouge.";
  } else if (blueScore.forfait && !redScore.forfait) {
    gagnant = `${redName} (Rouge)`;
    raison = "Victoire par forfait de Bleu.";
  } else if (redScore.hansokuMake && !blueScore.hansokuMake) {
    gagnant = `${blueName} (Bleu)`;
    raison = "Victoire par Hansoku-make de Rouge.";
  } else if (blueScore.hansokuMake && !redScore.hansokuMake) {
    gagnant = `${redName} (Rouge)`;
    raison = "Victoire par Hansoku-make de Bleu.";
  } else if (result?.result1 === "V" && result?.result2 === "D") {
    gagnant = `${redName} (Rouge)`;
    raison = "Victoire aux points.";
  } else if (result?.result1 === "D" && result?.result2 === "V") {
    gagnant = `${blueName} (Bleu)`;
    raison = "Victoire aux points.";
  } else if (result?.needExtraPeriod) {
    gagnant = "À déterminer (égalité parfaite)";
    raison =
      "Période supplémentaire nécessaire (2 minutes après 1 minute de repos, score conservé).";
  }

  return [
    `Gagnant : ${gagnant}`,
    `Discipline : Ju-Jitsu Combat`,
    `Score total : Rouge ${totalRed} – Bleu ${totalBlue}`,
    `Détail par parties : P1 ${redPts.part1}-${bluePts.part1} · P2 ${redPts.part2}-${bluePts.part2} · P3 ${redPts.part3}-${bluePts.part3}`,
    `Ippon par parties : Rouge [${redScore.part1Ippon || 0}/${redScore.part2Ippon || 0}/${redScore.part3Ippon || 0}] · Bleu [${blueScore.part1Ippon || 0}/${blueScore.part2Ippon || 0}/${blueScore.part3Ippon || 0}]`,
    `Résultat table : Rouge = ${result?.result1 ?? "-"} / Bleu = ${
      result?.result2 ?? "-"
    } · Points classement : ${result?.points1 ?? 0} – ${result?.points2 ?? 0}`,
    `Pénalités : Rouge Shido ${redScore.shido || 0}, Chui ${
      redScore.chui || 0
    }${redScore.hansokuMake ? ", Hansoku-make" : ""} · Bleu Shido ${
      blueScore.shido || 0
    }, Chui ${blueScore.chui || 0}${
      blueScore.hansokuMake ? ", Hansoku-make" : ""
    }`,
    `Temps de combat : ${chronoStr}${isExtraPeriod ? " (période supplémentaire)" : ""}`,
    raison ? `Raison : ${raison}` : null,
  ]
    .filter(Boolean)
    .join("\n");
}

/* ------------------------- UI helpers ------------------------- */

function Badge({ children, tone = "slate" }) {
  const tones = {
    slate: "bg-white/8 text-white border-white/10",
    emerald: "bg-emerald-500/15 text-emerald-200 border-emerald-400/20",
    yellow: "bg-yellow-400/15 text-yellow-200 border-yellow-300/20",
    red: "bg-red-500/15 text-red-200 border-red-400/20",
    blue: "bg-blue-500/15 text-blue-200 border-blue-400/20",
  };

  return (
    <span
      className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${tones[tone] || tones.slate}`}
    >
      {children}
    </span>
  );
}

function ActionButton({
  children,
  onClick,
  variant = "dark",
  disabled = false,
  className = "",
}) {
  const variants = {
    dark: "bg-slate-800 text-white hover:bg-slate-700 border border-white/10",
    green: "bg-emerald-500 text-slate-950 hover:bg-emerald-400",
    red: "bg-red-500 text-white hover:bg-red-400",
    blue: "bg-blue-500 text-white hover:bg-blue-400",
    orange: "bg-orange-500 text-slate-950 hover:bg-orange-400",
    amber: "bg-amber-500 text-slate-950 hover:bg-amber-400",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={[
        "rounded-xl px-3 py-2 text-sm font-bold transition",
        disabled ? "cursor-not-allowed opacity-45" : "",
        variants[variant] || variants.dark,
        className,
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function ChronoDisplay({ chrono, chronoClassName = "" }) {
  const minutes = String(Math.floor(chrono / 60)).padStart(2, "0");
  const seconds = String(chrono % 60).padStart(2, "0");
  return (
    <div
      className={`font-mono font-black tabular-nums text-[clamp(3rem,7vw,5.2rem)] leading-none ${chronoClassName}`}
    >
      {minutes}:{seconds}
    </div>
  );
}

function PartBlock({ partNumber, active, description, onClick }) {
  return (
    <div className="flex flex-col items-center w-[100px] sm:w-[120px]">
      <button
        type="button"
        onClick={onClick}
        className={[
          "w-[64px] h-[64px] sm:w-[76px] sm:h-[76px] rounded-2xl border-2 shadow-lg",
          "flex items-center justify-center text-3xl sm:text-4xl font-black transition",
          active
            ? "bg-orange-500 border-orange-400 text-white"
            : "bg-white border-white text-black hover:bg-white/90",
        ].join(" ")}
      >
        {partNumber}
      </button>

      <div
        className={`mt-2 text-center text-[10px] sm:text-[11px] leading-tight font-semibold ${
          active ? "text-white" : "text-white/72"
        }`}
      >
        {description}
      </div>
    </div>
  );
}

function JJCombatFighterPanel({
  label,
  competiteur,
  score,
  color = "red",
  isOsaeActive = false,
  currentPart,
  onAddTechnique,
  onRemoveTechnique,
  onPenalty,
  onUndoPenalty,
  onForfait,
}) {
  const { part1, part2, part3, total } = getJJPoints(score);
  const isRed = color === "red";
  const sideKey = isRed ? "red" : "blue";

  const theme = isRed
    ? {
        wrapper:
          "border-red-400/40 bg-[linear-gradient(135deg,rgba(127,29,29,0.95)_0%,rgba(220,38,38,0.78)_100%)]",
        soft: "bg-black/22 border-white/10",
        accentText: "text-red-100",
        accentSub: "text-red-100/75",
        sideTone: "text-red-200",
        primaryBtn: "red",
      }
    : {
        wrapper:
          "border-blue-400/40 bg-[linear-gradient(135deg,rgba(30,58,138,0.95)_0%,rgba(37,99,235,0.78)_100%)]",
        soft: "bg-black/22 border-white/10",
        accentText: "text-blue-100",
        accentSub: "text-blue-100/75",
        sideTone: "text-blue-200",
        primaryBtn: "blue",
      };

  return (
    <div
      className={`rounded-[24px] border ${theme.wrapper} p-4 shadow-2xl text-white flex flex-col`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="uppercase text-[11px] tracking-[0.22em] text-white/75">
            {label}
          </div>
          <div className="mt-2 text-[clamp(1.15rem,1.8vw,1.9rem)] font-black leading-tight break-words">
            {getCompetiteurDisplayName(competiteur, label)}
          </div>
        </div>

        <div className="rounded-2xl bg-black/20 px-4 py-3 text-right min-w-[104px]">
          <div className="text-[11px] uppercase tracking-[0.2em] text-white/65">
            Total
          </div>
          <div className="mt-1 text-[clamp(2.4rem,5vw,4.5rem)] font-black leading-none">
            {total}
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2">
        <div className={`rounded-2xl border p-3 ${theme.soft}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-bold uppercase tracking-wide">P1</span>
            <span className="text-lg font-black leading-none">{part1}</span>
          </div>
          <div className="mt-2 text-xs text-white/80">
            Ippon : {score.part1Ippon || 0}
          </div>
        </div>

        <div className={`rounded-2xl border p-3 ${theme.soft}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-bold uppercase tracking-wide">P2</span>
            <span className="text-lg font-black leading-none">{part2}</span>
          </div>
          <div className="mt-2 text-xs text-white/80">
            Ippon : {score.part2Ippon || 0}
          </div>
        </div>

        <div className={`rounded-2xl border p-3 ${theme.soft}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-bold uppercase tracking-wide">P3</span>
            <span className="text-lg font-black leading-none">{part3}</span>
          </div>
          <div className="mt-2 text-xs text-white/80">
            Ippon : {score.part3Ippon || 0}
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2">
        <div className={`rounded-2xl p-3 ${theme.soft}`}>
          <div className="text-[11px] uppercase tracking-[0.18em] text-white/65">
            Shido
          </div>
          <div className="mt-2 text-2xl font-black leading-none">
            {score.shido || 0}
          </div>
        </div>

        <div className={`rounded-2xl p-3 ${theme.soft}`}>
          <div className="text-[11px] uppercase tracking-[0.18em] text-white/65">
            Chui
          </div>
          <div className="mt-2 text-2xl font-black leading-none">
            {score.chui || 0}
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {score.hansokuMake && (
          <span className="inline-flex items-center rounded-full bg-red-600 px-3 py-1 text-xs font-black text-white">
            Hansoku-make
          </span>
        )}
        {score.forfait && (
          <span className="inline-flex items-center rounded-full bg-amber-500 px-3 py-1 text-xs font-black text-slate-950">
            Forfait
          </span>
        )}
        {isOsaeActive && (
          <span className="inline-flex items-center rounded-full bg-white px-3 py-1 text-xs font-black text-black">
            Osae-komi en cours
          </span>
        )}
      </div>

      {/* Commandes intégrées */}
      <div className="mt-5 border-t border-white/10 pt-4">
        <div className={`text-xs font-black uppercase tracking-[0.2em] ${theme.sideTone}`}>
          Techniques — Partie {currentPart}
        </div>

        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
          <ActionButton
            variant={theme.primaryBtn}
            onClick={() => onAddTechnique(sideKey, "wazaari")}
          >
            + Waza-ari
          </ActionButton>
          <ActionButton
            variant={theme.primaryBtn}
            onClick={() => onAddTechnique(sideKey, "ippon2")}
          >
            + Ippon 2 pts
          </ActionButton>
          <ActionButton
            variant={theme.primaryBtn}
            onClick={() => onAddTechnique(sideKey, "ippon3")}
          >
            + Ippon 3 pts
          </ActionButton>
        </div>

        <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2">
          <ActionButton
            variant="dark"
            onClick={() => onRemoveTechnique(sideKey, "wazaari")}
          >
            − Waza-ari
          </ActionButton>
          <ActionButton
            variant="dark"
            onClick={() => onRemoveTechnique(sideKey, "ippon2")}
          >
            − Ippon 2 pts
          </ActionButton>
          <ActionButton
            variant="dark"
            onClick={() => onRemoveTechnique(sideKey, "ippon3")}
          >
            − Ippon 3 pts
          </ActionButton>
        </div>

        <div className="mt-4 text-xs font-black uppercase tracking-[0.2em] text-white/75">
          Pénalités
        </div>

        <div className="mt-2 grid grid-cols-2 xl:grid-cols-5 gap-2">
          <ActionButton
            variant="amber"
            onClick={() => onPenalty(sideKey, "shido")}
          >
            + Shido
          </ActionButton>
          <ActionButton
            variant="orange"
            onClick={() => onPenalty(sideKey, "chui")}
          >
            + Chui
          </ActionButton>
          <ActionButton
            variant="dark"
            onClick={() => onUndoPenalty(sideKey, "shido")}
          >
            − Shido
          </ActionButton>
          <ActionButton
            variant="dark"
            onClick={() => onUndoPenalty(sideKey, "chui")}
          >
            − Chui
          </ActionButton>
          <ActionButton
            variant={theme.primaryBtn}
            onClick={() => onForfait(sideKey)}
            className="col-span-2 xl:col-span-1"
          >
            Forfait
          </ActionButton>
        </div>
      </div>
    </div>
  );
}

/* --------------------- Composant principal Admin --------------------- */

export default function ScoreboardAdminJJCombat({
  tapisId,
  competitionId,
  competiteur1,
  competiteur2,
  categorieAge,
  combatKey,
  discipline,
  onValidate,
  dureeCombat = 180,
}) {
  const [currentPart, setCurrentPart] = useState(1);

  const [redScore, setRedScore] = useState(() => createEmptyJJScore());
  const [blueScore, setBlueScore] = useState(() => createEmptyJJScore());

  const [redPenaltiesHistory, setRedPenaltiesHistory] = useState([]);
  const [bluePenaltiesHistory, setBluePenaltiesHistory] = useState([]);

  const [chrono, setChrono] = useState(dureeCombat);
  const [chronoMax, setChronoMax] = useState(dureeCombat);
  const [isChronoRunning, setIsChronoRunning] = useState(false);

  const [osaeFighter, setOsaeFighter] = useState(null);
  const [osaeSeconds, setOsaeSeconds] = useState(0);
  const [osaeRunning, setOsaeRunning] = useState(false);

  const [isExtraPeriod, setIsExtraPeriod] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);

  const titreDiscipline = discipline || "Ju-Jitsu Combat";
  const publicCombatLabel = useMemo(
    () => sanitizeCombatLabel(combatKey),
    [combatKey],
  );

  const liveResult = useMemo(() => {
    try {
      return calculateJJCombatResult(redScore, blueScore);
    } catch (error) {
      console.error("Erreur calcul live JJ Combat :", error);
      return null;
    }
  }, [redScore, blueScore]);

  const chronoPercent = useMemo(() => {
    if (!chronoMax) return 0;
    return Math.max(0, Math.min(100, (chrono / chronoMax) * 100));
  }, [chrono, chronoMax]);

  const showNeedExtraPeriodBanner = Boolean(
    liveResult?.needExtraPeriod && chrono <= 0,
  );

  const chronoColorClass = useMemo(() => {
    if (showNeedExtraPeriodBanner) return "text-yellow-300";
    if (chrono <= 10) return "text-red-400";
    if (chrono <= 30) return "text-amber-300";
    return "text-white";
  }, [chrono, showNeedExtraPeriodBanner]);

  useEffect(() => {
    if (!competitionId || !tapisId) return;
    const roomKey = `${competitionId}_${tapisId}`;
    socket.emit("join_combat", roomKey);
  }, [competitionId, tapisId]);

  useEffect(() => {
    if (!isChronoRunning) return;

    const id = setInterval(() => {
      setChrono((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          setIsChronoRunning(false);
          toast.info("⏰ Fin du temps réglementaire");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(id);
  }, [isChronoRunning]);

  useEffect(() => {
    if (!osaeRunning) return;

    const id = setInterval(() => {
      setOsaeSeconds((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(id);
  }, [osaeRunning]);

  useEffect(() => {
    setChrono(dureeCombat);
    setChronoMax(dureeCombat);
    setIsExtraPeriod(false);
  }, [dureeCombat]);

  useEffect(() => {
    if (!competitionId || !tapisId) return;

    const roomKey = `${competitionId}_${tapisId}`;

    socket.emit("score_update", {
      combatId: roomKey,
      data: {
        discipline: discipline || "Ju-Jitsu Combat",
        competiteur1,
        competiteur2,
        redScore,
        blueScore,
        currentPart,
        chrono,
        chronoMax,
        osaeFighter,
        osaeSeconds,
        osaeRunning,
        categorieAge: categorieAge || "",
        categoriePoids: "",
        combatLabel: publicCombatLabel,
        winnerColor:
          liveResult?.result1 === "V" && liveResult?.result2 === "D"
            ? "red"
            : liveResult?.result1 === "D" && liveResult?.result2 === "V"
              ? "blue"
              : null,
        needExtraPeriod: !!liveResult?.needExtraPeriod,
        message: isExtraPeriod ? "Période supplémentaire" : "",
      },
    });
  }, [
    competitionId,
    tapisId,
    discipline,
    competiteur1,
    competiteur2,
    redScore,
    blueScore,
    currentPart,
    chrono,
    chronoMax,
    osaeFighter,
    osaeSeconds,
    osaeRunning,
    categorieAge,
    publicCombatLabel,
    liveResult,
    isExtraPeriod,
  ]);

  const resetOsae = useCallback(() => {
    setOsaeRunning(false);
    setOsaeSeconds(0);
    setOsaeFighter(null);
  }, []);

  const handleStopOsae = useCallback(() => {
    setOsaeRunning(false);

    const fighter = osaeFighter;
    const seconds = osaeSeconds;

    if (!fighter || seconds < 10) {
      if (fighter && seconds > 0) {
        toast.info("Immobilisation < 10s : aucun point attribué.");
      }
      setOsaeFighter(null);
      setOsaeSeconds(0);
      return;
    }

    const partIndex = 3;

    if (seconds >= 15) {
      if (fighter === "red") {
        setRedScore((prev) => addTechnique(prev, partIndex, { kind: "ippon2" }));
      } else {
        setBlueScore((prev) =>
          addTechnique(prev, partIndex, { kind: "ippon2" }),
        );
      }
      toast.success("Ippon au sol (≥ 15s) attribué (2 pts).");
    } else {
      if (fighter === "red") {
        setRedScore((prev) => addTechnique(prev, partIndex, { kind: "wazaari" }));
      } else {
        setBlueScore((prev) =>
          addTechnique(prev, partIndex, { kind: "wazaari" }),
        );
      }
      toast.info("Waza-ari au sol (10–14s) attribué (1 pt).");
    }

    setOsaeFighter(null);
    setOsaeSeconds(0);
  }, [osaeFighter, osaeSeconds]);

  useEffect(() => {
    if (!osaeRunning || !osaeFighter) return;
    if (osaeSeconds >= 15) {
      handleStopOsae();
    }
  }, [osaeSeconds, osaeRunning, osaeFighter, handleStopOsae]);

  const handleStartPause = () => {
    if (chrono <= 0) {
      toast.info("Le chrono est à 0. Réinitialise-le ou lance la période supplémentaire.");
      return;
    }
    setIsChronoRunning((prev) => !prev);
  };

  const handleResetChrono = () => {
    setIsChronoRunning(false);
    setChrono(chronoMax);
  };

  const handleResetOsae = () => {
    resetOsae();
  };

  const resetAll = () => {
    setIsChronoRunning(false);
    setChrono(dureeCombat);
    setChronoMax(dureeCombat);
    setIsExtraPeriod(false);
    setRedScore(createEmptyJJScore());
    setBlueScore(createEmptyJJScore());
    setRedPenaltiesHistory([]);
    setBluePenaltiesHistory([]);
    setCurrentPart(1);
    setModalVisible(false);
    resetOsae();
    toast.success("Scoreboard Ju-Jitsu Combat réinitialisé.");
  };

  const handleStartOsae = (who) => {
    if (currentPart !== 3) {
      setCurrentPart(3);
    }
    setOsaeFighter(who);
    setOsaeSeconds(0);
    setOsaeRunning(true);
  };

  const handleAddTechnique = (who, kind) => {
    if (currentPart < 1 || currentPart > 3) return;

    if (who === "red") {
      setRedScore((prev) => addTechnique(prev, currentPart, { kind }));
    } else {
      setBlueScore((prev) => addTechnique(prev, currentPart, { kind }));
    }
  };

  const handleRemoveTechnique = (who, kind) => {
    if (currentPart < 1 || currentPart > 3) return;

    if (who === "red") {
      setRedScore((prev) => removeTechnique(prev, currentPart, { kind }));
    } else {
      setBlueScore((prev) => removeTechnique(prev, currentPart, { kind }));
    }
  };

  const handlePenalty = (penalized, type) => {
    const isRedPenalized = penalized === "red";
    const penalizedState = isRedPenalized ? redScore : blueScore;
    const opponent = isRedPenalized ? "blue" : "red";

    const prevChui = penalizedState.chui || 0;
    const prevPenaltyPoints = penalizedState.penaltyPoints || 0;

    let pointsToGive = 0;

    if (type === "shido") {
      pointsToGive = 1;
    } else if (type === "chui") {
      if (prevChui === 0) {
        pointsToGive = 3;
      } else {
        pointsToGive = 0;
      }
    }

    if (isRedPenalized) {
      setRedScore((prev) => addPenalty(prev, { type }));
    } else {
      setBlueScore((prev) => addPenalty(prev, { type }));
    }

    if (pointsToGive > 0) {
      if (opponent === "red") {
        setRedScore((prev) => {
          let next = prev;
          for (let i = 0; i < pointsToGive; i += 1) {
            next = addTechnique(next, currentPart, { kind: "wazaari" });
          }
          return next;
        });
      } else {
        setBlueScore((prev) => {
          let next = prev;
          for (let i = 0; i < pointsToGive; i += 1) {
            next = addTechnique(next, currentPart, { kind: "wazaari" });
          }
          return next;
        });
      }
    }

    const entry = {
      type,
      part: currentPart,
      pointsToGive,
    };

    if (isRedPenalized) {
      setRedPenaltiesHistory((prev) => [...prev, entry]);
    } else {
      setBluePenaltiesHistory((prev) => [...prev, entry]);
    }

    const newChui = type === "chui" ? prevChui + 1 : prevChui;
    let newPenaltyPoints = prevPenaltyPoints;
    if (type === "shido") newPenaltyPoints += 1;
    if (type === "chui" && prevChui === 0) newPenaltyPoints += 3;

    const label = isRedPenalized ? "Rouge" : "Bleu";

    if (type === "shido") {
      toast.warn(`Shido pour ${label} — ${pointsToGive} pt à l'adversaire.`);
    } else if (type === "chui") {
      if (prevChui === 0) {
        toast.error(`1er Chui pour ${label} — 3 pts à l'adversaire.`);
      } else {
        toast.error(`2ème Chui pour ${label} — Hansoku-make.`);
      }
    }

    if (newChui >= 2 || newPenaltyPoints >= 6) {
      toast.error(`Hansoku-make pour ${label} (pénalités).`);
    }
  };

  const handleUndoPenalty = (penalized, type) => {
    const isRedPenalized = penalized === "red";
    const history = isRedPenalized ? redPenaltiesHistory : bluePenaltiesHistory;
    const opponent = isRedPenalized ? "blue" : "red";

    let idx = -1;
    for (let i = history.length - 1; i >= 0; i -= 1) {
      if (history[i].type === type) {
        idx = i;
        break;
      }
    }

    if (idx === -1) {
      toast.info(
        `Aucune pénalité ${type} à annuler pour ${
          isRedPenalized ? "Rouge" : "Bleu"
        }.`,
      );
      return;
    }

    const entry = history[idx];
    const newHistory = history.slice(0, idx).concat(history.slice(idx + 1));

    if (isRedPenalized) {
      setRedScore((prev) => removePenalty(prev, { type }));
      setRedPenaltiesHistory(newHistory);
    } else {
      setBlueScore((prev) => removePenalty(prev, { type }));
      setBluePenaltiesHistory(newHistory);
    }

    if (entry.pointsToGive > 0) {
      if (opponent === "red") {
        setRedScore((prev) => {
          let next = prev;
          for (let i = 0; i < entry.pointsToGive; i += 1) {
            next = removeTechnique(next, entry.part, { kind: "wazaari" });
          }
          return next;
        });
      } else {
        setBlueScore((prev) => {
          let next = prev;
          for (let i = 0; i < entry.pointsToGive; i += 1) {
            next = removeTechnique(next, entry.part, { kind: "wazaari" });
          }
          return next;
        });
      }
    }

    toast.success(
      `Pénalité ${type} annulée pour ${isRedPenalized ? "Rouge" : "Bleu"}.`,
    );
  };

  const handleForfait = (who) => {
    if (
      !window.confirm(
        `Confirmer le forfait du combattant ${
          who === "red" ? "Rouge" : "Bleu"
        } ?`,
      )
    ) {
      return;
    }

    if (who === "red") {
      setRedScore((prev) => setForfait(prev, true));
    } else {
      setBlueScore((prev) => setForfait(prev, true));
    }

    toast.warn(
      `Forfait ${
        who === "red" ? "Rouge" : "Bleu"
      } — n'oublie pas de valider le combat.`,
    );
  };

  const handleRequestExtraPeriod = () => {
    setIsChronoRunning(false);
    setChronoMax(120);
    setChrono(120);
    setIsExtraPeriod(true);
    resetOsae();
    setModalVisible(false);
    toast.info("Période supplémentaire JJ Combat : 2 minutes.");
  };

  const handleValidate = () => {
    const result = liveResult || calculateJJCombatResult(redScore, blueScore);

    if (result?.needExtraPeriod) {
      toast.info(
        "Égalité parfaite : prévoir une période supplémentaire de 2 minutes après 1 minute de repos.",
      );
      return;
    }

    const payload = {
      result1: result.result1,
      result2: result.result2,
      points1: result.points1,
      points2: result.points2,
      forfait1: redScore.forfait || false,
      forfait2: blueScore.forfait || false,
      hansoku1: redScore.hansokuMake || false,
      hansoku2: blueScore.hansokuMake || false,
      details_combat: buildDetailsCombatJJ({
        redScore,
        blueScore,
        result,
        competiteur1,
        competiteur2,
        chrono,
        chronoMax,
        isExtraPeriod,
      }),
    };

    if (typeof onValidate === "function") {
      onValidate(payload);
    } else {
      console.log("[JJCombat] Résultat calculé :", payload);
      toast.success("Résultat Ju-Jitsu Combat calculé (voir console).");
    }
  };

  const partConfig = PART_CONFIG[currentPart] || PART_CONFIG[1];
  const nextWinnerColor =
    liveResult?.result1 === "V" && liveResult?.result2 === "D"
      ? "red"
      : liveResult?.result1 === "D" && liveResult?.result2 === "V"
        ? "blue"
        : null;

  return (
    <div className="h-full min-h-0 bg-black text-white overflow-hidden">
      <ToastContainer position="top-center" />

      <div className="h-full min-h-0 overflow-y-auto px-3 md:px-4 py-3 pb-28">
        <div className="mx-auto w-full max-w-[1650px] space-y-4">
          {/* Header */}
          <div className="rounded-[24px] border border-white/10 bg-slate-950 px-4 md:px-5 py-4">
            <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-[0.25em] text-white/50">
                  Tableau de score
                </div>
                <div className="mt-2 text-[clamp(1.2rem,2.2vw,2rem)] font-black leading-tight">
                  {titreDiscipline}
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  <Badge tone="emerald">Tapis {tapisId}</Badge>
                  {!!categorieAge && <Badge tone="yellow">Cat. {categorieAge}</Badge>}
                  {isExtraPeriod && (
                    <Badge tone="yellow">Période supplémentaire</Badge>
                  )}
                  {nextWinnerColor === "red" && (
                    <Badge tone="red">Avantage Rouge</Badge>
                  )}
                  {nextWinnerColor === "blue" && (
                    <Badge tone="blue">Avantage Bleu</Badge>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 xl:justify-end">
                <ActionButton variant="dark" onClick={resetAll}>
                  Réinitialiser
                </ActionButton>
                <ActionButton
                  variant="green"
                  onClick={() => setModalVisible(true)}
                >
                  Valider le combat
                </ActionButton>
              </div>
            </div>
          </div>

          {/* Main scoreboard area */}
          <div className="grid grid-cols-1 2xl:grid-cols-[1fr_330px_1fr] gap-4 items-start">
            <JJCombatFighterPanel
              label="Rouge"
              competiteur={competiteur1}
              score={redScore}
              color="red"
              isOsaeActive={osaeFighter === "red"}
              currentPart={currentPart}
              onAddTechnique={handleAddTechnique}
              onRemoveTechnique={handleRemoveTechnique}
              onPenalty={handlePenalty}
              onUndoPenalty={handleUndoPenalty}
              onForfait={handleForfait}
            />

            <div className="rounded-[24px] border border-white/10 bg-slate-950 p-4 flex flex-col gap-4">
              <div className="text-center">
                <div className="text-[11px] uppercase tracking-[0.22em] text-white/55">
                  Chronomètre
                </div>
                <div className="mt-3">
                  <ChronoDisplay chrono={chrono} chronoClassName={chronoColorClass} />
                </div>

                <div className="mt-4 h-4 w-full overflow-hidden rounded-full bg-white/10">
                  <div
                    className="h-full bg-emerald-400 transition-all duration-300"
                    style={{ width: `${chronoPercent}%` }}
                  />
                </div>

                <div className="mt-2 text-xs font-semibold uppercase tracking-[0.18em] text-white/50">
                  Temps cible : {formatSeconds(chronoMax)}
                </div>

                <div className="mt-4 grid grid-cols-2 gap-2">
                  <ActionButton variant="green" onClick={handleStartPause}>
                    {isChronoRunning ? "Pause" : "Start"}
                  </ActionButton>
                  <ActionButton variant="dark" onClick={handleResetChrono}>
                    Reset chrono
                  </ActionButton>
                </div>
              </div>

              <div className="border-t border-white/10 pt-4">
                <div className="text-center text-[11px] uppercase tracking-[0.22em] text-white/55">
                  Phase active
                </div>
                <div className="mt-4 flex flex-wrap items-start justify-center gap-3">
                  {[1, 2, 3].map((p) => (
                    <PartBlock
                      key={p}
                      partNumber={p}
                      active={currentPart === p}
                      description={PART_CONFIG[p].description}
                      onClick={() => setCurrentPart(p)}
                    />
                  ))}
                </div>
              </div>

              <div className="border-t border-white/10 pt-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.22em] text-white/55">
                      Osae-komi
                    </div>
                    <div className="mt-2 text-[clamp(1.9rem,4vw,3.1rem)] font-mono font-black leading-none">
                      {String(osaeSeconds).padStart(2, "0")}s
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-xs font-semibold text-white/70">
                      Partie 3 uniquement
                    </div>
                    <div className="mt-1 text-[11px] text-white/45">
                      ≥ 10s : 1 pt • ≥ 15s : 2 pts
                    </div>
                  </div>
                </div>

                {osaeFighter && (
                  <div className="mt-3">
                    <Badge tone={osaeFighter === "red" ? "red" : "blue"}>
                      Immobilisation en cours :{" "}
                      {osaeFighter === "red" ? "Rouge" : "Bleu"}
                    </Badge>
                  </div>
                )}

                <div className="mt-4 grid grid-cols-2 gap-2">
                  <ActionButton
                    variant="red"
                    onClick={() => handleStartOsae("red")}
                  >
                    Osae Rouge
                  </ActionButton>
                  <ActionButton
                    variant="blue"
                    onClick={() => handleStartOsae("blue")}
                  >
                    Osae Bleu
                  </ActionButton>
                  <ActionButton
                    variant="amber"
                    onClick={handleStopOsae}
                    disabled={!osaeFighter || (!osaeRunning && osaeSeconds === 0)}
                  >
                    Stop Osae
                  </ActionButton>
                  <ActionButton variant="dark" onClick={handleResetOsae}>
                    Reset Osae
                  </ActionButton>
                </div>
              </div>

              {showNeedExtraPeriodBanner && (
                <div className="rounded-2xl border border-yellow-300/20 bg-yellow-400/10 p-4">
                  <div className="text-sm font-black text-yellow-200">
                    HIKIWAKE — période supplémentaire nécessaire
                  </div>
                  <div className="mt-1 text-xs text-yellow-100/80">
                    Ouvre la validation pour lancer la période supplémentaire.
                  </div>
                </div>
              )}

              {!showNeedExtraPeriodBanner && !!publicCombatLabel && (
                <div className="text-center text-xs uppercase tracking-[0.18em] text-white/45">
                  {publicCombatLabel}
                </div>
              )}

              <div className="text-center">
                <div className="text-sm font-bold text-emerald-300">
                  {partConfig.label}
                </div>
                <div className="mt-1 text-xs text-white/60">
                  {partConfig.description}
                </div>
              </div>
            </div>

            <JJCombatFighterPanel
              label="Bleu"
              competiteur={competiteur2}
              score={blueScore}
              color="blue"
              isOsaeActive={osaeFighter === "blue"}
              currentPart={currentPart}
              onAddTechnique={handleAddTechnique}
              onRemoveTechnique={handleRemoveTechnique}
              onPenalty={handlePenalty}
              onUndoPenalty={handleUndoPenalty}
              onForfait={handleForfait}
            />
          </div>

          {/* Bottom actions */}
          {/* <div className="flex flex-wrap items-center justify-between gap-3 rounded-[24px] border border-white/10 bg-slate-950 px-4 md:px-5 py-4">
            <div className="text-sm text-white/60">
              Les commandes techniques et pénalités sont intégrées dans chaque panneau combattant.
            </div>

            <div className="flex flex-wrap gap-2">
              <ActionButton variant="dark" onClick={resetAll}>
                Réinitialiser tout
              </ActionButton>
              <ActionButton
                variant="green"
                onClick={() => setModalVisible(true)}
              >
                Valider le combat
              </ActionButton>
            </div>
          </div> */}
        </div>
      </div>

      <DetermineWinnerModalJJCombat
        isOpen={modalVisible}
        onClose={() => setModalVisible(false)}
        onValidate={handleValidate}
        onRequestExtraPeriod={handleRequestExtraPeriod}
        competiteur1={competiteur1}
        competiteur2={competiteur2}
        redScore={redScore}
        blueScore={blueScore}
        autoResult={liveResult}
        chrono={chrono}
      />
    </div>
  );
}